import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RefreshCw, Loader2, Cpu, Search, X, Square, CheckSquare, ChevronUp, ChevronDown, Download } from 'lucide-react';
import { API } from '../../App';

const TIER_COLORS = { free: '#22C55E', budget: '#00D4FF', standard: '#F2A900', premium: '#FF4D00' };
const TIER_ORDER = ['free', 'budget', 'standard', 'premium'];
const TIER_RANK = { free: 0, budget: 1, standard: 2, premium: 3 };

function fmtK(n) { return n ? `${Math.round(n / 1000)}k` : '—'; }
function fmtIq(s) { return s != null ? (s / 10).toFixed(1) : '—'; }
// #1162: format OR prices stored as $/token to a compact $/M-token string.
// Mirrors OR's own model-card display.
function fmtPriceM(perToken) {
  if (perToken == null || perToken === 0) return '—';
  const perMillion = Number(perToken) * 1_000_000;
  if (perMillion < 1) return perMillion.toFixed(2);
  if (perMillion < 10) return perMillion.toFixed(1);
  return Math.round(perMillion).toString();
}
function fmtPriceTitle(perToken) {
  if (perToken == null || perToken === 0) return 'Free';
  const perMillion = Number(perToken) * 1_000_000;
  return `$${perMillion.toFixed(4)} per million tokens`;
}
const NEW_THRESHOLD_DAYS = 30;
function isNew(m) {
  const d = m.or_created_at || m.created_at;
  if (!d) return false;
  return (Date.now() - new Date(d).getTime()) < NEW_THRESHOLD_DAYS * 86400000;
}

export default function ModelRosterAdmin() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);

  // Active panel
  const [activeTier, setActiveTier] = useState('');

  // Available panel filters
  const [availTier, setAvailTier] = useState('');
  const [search, setSearch] = useState('');
  const [minIq, setMinIq] = useState(0);
  const [hasIqOnly, setHasIqOnly] = useState(false);
  const [newOnly, setNewOnly] = useState(false);
  const [minCtx, setMinCtx] = useState(0);
  const [minOut, setMinOut] = useState(0);
  const [capFilter, setCapFilter] = useState('');

  // Sorting (shared)
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Selection (available panel only)
  const [selected, setSelected] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const loadModels = () => {
    setLoading(true);
    API.get('/api/models/roster?active_only=false')
      .then(r => setModels(r.data.models || []))
      .catch(() => setModels([]))
      .finally(() => setLoading(false));
  };

  const loadSyncStatus = () => {
    API.get('/api/models/sync-status').then(r => setSyncStatus(r.data)).catch(() => {});
  };

  useEffect(() => { loadModels(); loadSyncStatus(); }, []);

  const sync = async () => {
    setSyncing(true); setSyncResult(null);
    try {
      const r = await API.post('/api/admin/models/sync');
      setSyncResult(r.data);
      if (r.data.synced_at) {
        setSyncStatus(prev => ({ ...prev, last_sync: r.data.synced_at }));
      }
      loadModels(); loadSyncStatus();
    } catch (e) { setSyncResult({ error: e.response?.data?.detail || 'Sync failed' }); }
    setSyncing(false);
  };

  const formatTimeAgo = (iso) => {
    if (!iso) return 'Never';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  // Sorting
  const applySort = useCallback((list) => {
    if (!sortField) return list;
    return [...list].sort((a, b) => {
      let av, bv;
      if (sortField === 'display_name') { av = (a.display_name || a.slug || '').toLowerCase(); bv = (b.display_name || b.slug || '').toLowerCase(); }
      else if (sortField === 'tier') { av = TIER_RANK[a.tier] ?? 99; bv = TIER_RANK[b.tier] ?? 99; }
      else if (sortField === 'intelligence_score') { av = a.intelligence_score ?? -1; bv = b.intelligence_score ?? -1; }
      else if (sortField === 'context_window') { av = a.context_window ?? 0; bv = b.context_window ?? 0; }
      else if (sortField === 'max_output_tokens') { av = a.max_output_tokens ?? 0; bv = b.max_output_tokens ?? 0; }
      else if (sortField === 'prompt_price_per_token') { av = Number(a.prompt_price_per_token ?? 0); bv = Number(b.prompt_price_per_token ?? 0); }
      else if (sortField === 'completion_price_per_token') { av = Number(a.completion_price_per_token ?? 0); bv = Number(b.completion_price_per_token ?? 0); }
      else if (sortField === 'or_created_at') {
        av = a.or_created_at ? new Date(a.or_created_at).getTime() : 0;
        bv = b.or_created_at ? new Date(b.or_created_at).getTime() : 0;
      }
      else return 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // Stats
  const stats = useMemo(() => {
    const tiers = {};
    models.forEach(m => { tiers[m.tier] = (tiers[m.tier] || 0) + 1; });
    return { total: models.length, active: models.filter(m => m.catalog_enabled && !m.deprecated_at).length, tiers,
      thinking: models.filter(m => m.supports_thinking).length,
      tools: models.filter(m => m.supports_function_calling).length,
      images: models.filter(m => m.supports_images).length };
  }, [models]);

  // Active panel models — admin-enabled and not OR-deprecated
  const activeModels = useMemo(() => applySort(
    models.filter(m => m.catalog_enabled && !m.deprecated_at && (!activeTier || m.tier === activeTier))
  ), [models, activeTier, applySort]);

  const activeTierCounts = useMemo(() => {
    const c = {};
    models.filter(m => m.catalog_enabled && !m.deprecated_at).forEach(m => { c[m.tier] = (c[m.tier] || 0) + 1; });
    return c;
  }, [models]);

  // Available panel models — not admin-enabled, and not OR-deprecated
  const availableModels = useMemo(() => applySort(
    models.filter(m => {
      if (m.catalog_enabled || m.deprecated_at) return false;
      if (availTier && m.tier !== availTier) return false;
      if (hasIqOnly && m.intelligence_score == null) return false;
      if (newOnly && !isNew(m)) return false;
      if (minIq > 0 && (m.intelligence_score == null || m.intelligence_score < minIq)) return false;
      if (minCtx > 0 && (m.context_window == null || m.context_window < minCtx)) return false;
      if (minOut > 0 && (m.max_output_tokens == null || m.max_output_tokens < minOut)) return false;
      if (capFilter === 'thinking' && !m.supports_thinking) return false;
      if (capFilter === 'function_calling' && !m.supports_function_calling) return false;
      if (capFilter === 'images' && !m.supports_images) return false;
      if (search) {
        const s = search.toLowerCase();
        return (m.slug || '').toLowerCase().includes(s) ||
               (m.display_name || '').toLowerCase().includes(s) ||
               (m.provider || '').toLowerCase().includes(s);
      }
      return true;
    })
  ), [models, availTier, hasIqOnly, newOnly, minIq, minCtx, minOut, capFilter, search, applySort]);

  const availTierCounts = useMemo(() => {
    const c = {};
    models.filter(m => !m.catalog_enabled && !m.deprecated_at).forEach(m => { c[m.tier] = (c[m.tier] || 0) + 1; });
    return c;
  }, [models]);

  useEffect(() => { setSelected(new Set()); }, [search, availTier, hasIqOnly, newOnly, minIq, minCtx, minOut, capFilter]);

  // Actions
  const deactivate = async (model) => {
    try { await API.put(`/api/admin/models/roster/${model.id}`, { is_active: false }); loadModels(); loadSyncStatus(); } catch {}
  };
  const activate = async (model) => {
    try { await API.put(`/api/admin/models/roster/${model.id}`, { is_active: true }); loadModels(); loadSyncStatus(); } catch {}
  };

  const bulkActivate = async () => {
    if (!selected.size) return;
    setBulkUpdating(true);
    try { await API.put('/api/admin/models/roster/bulk', { model_ids: Array.from(selected), is_active: true }); } catch {}
    setSelected(new Set()); setBulkUpdating(false); loadModels(); loadSyncStatus();
  };

  const bulkSetTierActive = async (tiers, active) => {
    setBulkUpdating(true);
    try { await API.put('/api/admin/models/roster/bulk-by-tier', { tiers, is_active: active }); } catch {}
    setBulkUpdating(false); loadModels(); loadSyncStatus();
  };

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => {
    setSelected(selected.size === availableModels.length ? new Set() : new Set(availableModels.map(m => m.id)));
  };

  // Sub-components
  const TierPills = ({ active, onSelect, counts }) => (
    <div className="flex gap-1 flex-wrap">
      <button onClick={() => onSelect('')}
        className={`px-2.5 py-0.5 text-[9px] font-mono transition-colors ${!active ? 'bg-[#FF4D00] text-black' : 'border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F5F1E8]'}`}>
        All ({Object.values(counts).reduce((a, b) => a + b, 0)})
      </button>
      {TIER_ORDER.filter(t => counts[t]).map(t => (
        <button key={t} onClick={() => onSelect(active === t ? '' : t)}
          className={`px-2.5 py-0.5 text-[9px] font-mono transition-colors ${active === t ? 'text-black' : 'border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F5F1E8]'}`}
          style={active === t ? { backgroundColor: TIER_COLORS[t] } : {}}>
          {t.charAt(0).toUpperCase() + t.slice(1)} ({counts[t]})
        </button>
      ))}
    </div>
  );

  const SortTh = ({ field, label, className = '' }) => {
    const active = sortField === field;
    return (
      <button onClick={() => toggleSort(field)}
        className={`flex items-center gap-0.5 hover:text-[#F5F1E8] transition-colors ${active ? 'text-[#FF4D00]' : ''} ${className}`}>
        {label}
        {active ? (sortDir === 'asc' ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />) : null}
      </button>
    );
  };

  const ColHeaders = ({ showCheckbox = false }) => (
    <div className="flex items-center gap-2 px-2 py-1 text-[8px] font-mono text-[#A0A0A0] border-b border-[#2A2A2A]">
      {showCheckbox
        ? <button onClick={toggleSelectAll} className="w-3.5 shrink-0">
            {selected.size === availableModels.length && availableModels.length > 0
              ? <CheckSquare className="w-3.5 h-3.5 text-[#FF4D00]" />
              : <Square className="w-3.5 h-3.5" />}
          </button>
        : <span className="w-3.5 shrink-0" />}
      <SortTh field="display_name" label="MODEL" className="flex-1 text-left" />
      <SortTh field="or_created_at" label="ADDED" className="w-12 text-right justify-end" />
      <SortTh field="intelligence_score" label="IQ" className="w-9 text-right justify-end" />
      <SortTh field="tier" label="TIER" className="w-14 text-right justify-end" />
      <SortTh field="context_window" label="CTX" className="w-9 text-right justify-end" />
      <SortTh field="max_output_tokens" label="OUT" className="w-9 text-right justify-end" />
      <SortTh field="prompt_price_per_token" label="IN $/M" className="w-12 text-right justify-end" />
      <SortTh field="completion_price_per_token" label="OUT $/M" className="w-12 text-right justify-end" />
      <span className="w-8 shrink-0" />
    </div>
  );

  const ModelRow = ({ m, showCheckbox = false }) => (
    <div className={`flex items-center gap-2 px-2 py-1.5 bg-[#0F0F0F] border transition-colors ${
      showCheckbox && selected.has(m.id) ? 'border-[#FF4D00]' : 'border-[#2A2A2A]'
    }`}>
      {showCheckbox
        ? <button onClick={() => toggleSelect(m.id)} className="w-3.5 shrink-0">
            {selected.has(m.id)
              ? <CheckSquare className="w-3.5 h-3.5 text-[#FF4D00]" />
              : <Square className="w-3.5 h-3.5 text-[#A0A0A0]" />}
          </button>
        : <Cpu className="w-3 h-3 text-[#A0A0A0] shrink-0" />}
      <span className="text-xs font-mono flex-1 truncate flex items-center gap-1.5" title={m.slug}>
        {m.display_name || m.slug}
        {isNew(m) && <span className="text-[7px] font-mono px-1 py-0.5 bg-[#22C55E]/20 text-[#22C55E] border border-[#22C55E]/40 shrink-0">NEW</span>}
      </span>
      <span className="w-12 text-right text-[8px] font-mono text-[#555] shrink-0"
        title={m.or_created_at ? `OR added: ${new Date(m.or_created_at).toLocaleDateString()}` : 'OR date unknown'}>
        {m.or_created_at ? new Date(m.or_created_at).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '—'}
      </span>
      <span className={`w-9 text-right text-[8px] font-mono shrink-0 ${m.intelligence_score != null ? 'text-[#F2A900]' : 'text-[#3A3A3A]'}`}
        title="Intelligence Index — artificialanalysis.ai">
        {fmtIq(m.intelligence_score)}
      </span>
      <span className="w-14 text-right shrink-0">
        <span className="text-[8px] font-mono px-1 py-0.5 border" style={{
          color: TIER_COLORS[m.tier] || '#A0A0A0',
          borderColor: (TIER_COLORS[m.tier] || '#A0A0A0') + '40'
        }}>{m.tier}</span>
      </span>
      <span className="w-9 text-right text-[8px] font-mono text-[#A0A0A0] shrink-0">{fmtK(m.context_window)}</span>
      <span className="w-9 text-right text-[8px] font-mono text-[#A0A0A0] shrink-0">{fmtK(m.max_output_tokens)}</span>
      <span className="w-12 text-right text-[8px] font-mono text-[#A0A0A0] shrink-0"
            title={fmtPriceTitle(m.prompt_price_per_token)}>{fmtPriceM(m.prompt_price_per_token)}</span>
      <span className="w-12 text-right text-[8px] font-mono text-[#A0A0A0] shrink-0"
            title={fmtPriceTitle(m.completion_price_per_token)}>{fmtPriceM(m.completion_price_per_token)}</span>
      {!showCheckbox && (
        <button onClick={() => deactivate(m)} className="w-8 flex justify-center shrink-0 text-[#A0A0A0] hover:text-[#EF4444]" title="Remove from catalog">
          <X className="w-3 h-3" />
        </button>
      )}
      {showCheckbox && <span className="w-8 shrink-0" />}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold font-['Barlow_Condensed']">
          <Cpu className="w-4 h-4 inline mr-2 text-[#00D4FF]" />
          Model Roster
        </h3>
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-end gap-0.5">
            <span className="text-[9px] font-mono text-[#A0A0A0]">Last sync: {formatTimeAgo(syncStatus?.last_sync)}</span>
            <span className="text-[8px] font-mono text-[#A0A0A0]">
              IQ via <a href="https://artificialanalysis.ai" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#F5F1E8]">artificialanalysis.ai</a>
            </span>
          </div>
          <button onClick={sync} disabled={syncing}
            className="px-3 py-1.5 text-xs font-mono bg-[#00D4FF] text-black disabled:opacity-50 flex items-center gap-1.5 min-h-[36px]"
            title="Sync models + IQ scores from OpenRouter and Artificial Analysis">
            {syncing ? <><Loader2 className="w-3 h-3 animate-spin" /> Syncing...</> : <><RefreshCw className="w-3 h-3" /> Sync Models</>}
          </button>
        </div>
      </div>

      {syncResult && (
        <div className={`p-2 border text-xs font-mono ${syncResult.error ? 'border-[#EF4444] text-[#EF4444]' : 'border-[#22C55E] text-[#22C55E]'}`}>
          {syncResult.error
            ? syncResult.error
            : <>{`${syncResult.upserted || 0} synced · ${syncResult.deprecated || 0} deprecated · ${syncResult.total} total`}
                {syncResult.iq_matched != null && <span className="text-[#F2A900]">{` · IQ: ${syncResult.iq_matched}/${syncResult.iq_roster} matched`}</span>}</>}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: 'Total', val: stats.total, color: '#F5F1E8' },
          { label: 'Active', val: stats.active, color: '#22C55E' },
          { label: 'Available', val: stats.total - stats.active, color: '#A0A0A0' },
          { label: 'IQ Scored', val: models.filter(m => m.intelligence_score != null).length, color: '#F2A900' },
        ].map(({ label, val, color }) => (
          <div key={label} className="p-2 bg-[#0F0F0F] border border-[#2A2A2A] text-center">
            <p className="text-base font-bold" style={{ color }}>{val}</p>
            <p className="text-[8px] text-[#A0A0A0] font-mono">{label}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#A0A0A0] py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading roster...</div>
      ) : (
        <>
          {/* ── ACTIVE MODELS ── */}
          <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-mono text-[#F2A900]">ACTIVE MODELS ({activeModels.length})</h3>
              <div className="flex items-center gap-1">
                {['xlsx', 'csv'].map((fmt) => (
                  <button
                    key={fmt}
                    onClick={async () => {
                      try {
                        const res = await API.get(`/api/admin/models/export?format=${fmt}&scope=active`, { responseType: 'blob' });
                        const blob = new Blob([res.data]);
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = res.headers['content-disposition']?.match(/filename="?([^"]+)"?/)?.[1] || `active_models.${fmt}`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        console.error(`Active models ${fmt} export failed:`, err);
                      }
                    }}
                    className="flex items-center gap-1 px-2 py-0.5 text-[8px] font-mono uppercase border border-[#2A2A2A] text-[#A0A0A0] hover:border-[#F2A900] hover:text-[#F2A900] transition-colors"
                    data-testid={`export-active-models-${fmt}`}
                    title={`Download active models as ${fmt.toUpperCase()}`}
                  >
                    <Download className="w-3 h-3" />
                    {fmt}
                  </button>
                ))}
              </div>
            </div>
            <TierPills active={activeTier} onSelect={setActiveTier} counts={activeTierCounts} />
            {/* Bulk catalog actions — visually separated from tier filters above */}
            <div className="flex items-center gap-1 mt-2 pt-2 border-t border-[#2A2A2A]">
              <span className="text-[8px] font-mono text-[#555] mr-1">QUICK ADD:</span>
              <button onClick={() => bulkSetTierActive(['premium'], true)} disabled={bulkUpdating}
                className="px-2 py-0.5 text-[8px] font-mono border border-[#FF4D00]/40 text-[#FF4D00] hover:bg-[#FF4D00] hover:text-black disabled:opacity-50 transition-colors">
                +Premium
              </button>
              <button onClick={() => bulkSetTierActive(['standard', 'premium'], true)} disabled={bulkUpdating}
                className="px-2 py-0.5 text-[8px] font-mono border border-[#F2A900]/40 text-[#F2A900] hover:bg-[#F2A900] hover:text-black disabled:opacity-50 transition-colors">
                +Std+Prem
              </button>
              <span className="flex-1" />
              <button
                onClick={() => {
                  if (window.confirm(`Remove all ${activeModels.length} active models from the catalog? They will move to Available Models.`)) {
                    bulkSetTierActive(['free', 'budget', 'standard', 'premium'], false);
                  }
                }}
                disabled={bulkUpdating || activeModels.length === 0}
                className="px-2 py-0.5 text-[8px] font-mono border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444] hover:text-white disabled:opacity-50 transition-colors">
                Remove All
              </button>
            </div>
            {activeModels.length === 0 ? (
              <p className="text-xs text-[#A0A0A0] py-3">No active models. Use the Available Models section below to add models to the catalog.</p>
            ) : (
              /* #1044 — header sticky inside the same scroll container as the
                 rows so the scrollbar gutter affects both equally; otherwise
                 the body inset by scrollbar-width drifts right of the header. */
              <div className="mt-2 max-h-56 overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
                <div className="sticky top-0 bg-[#1A1A1A] z-10">
                  <ColHeaders showCheckbox={false} />
                </div>
                <div className="space-y-0.5 mt-0.5">
                  {activeModels.map(m => <ModelRow key={m.id} m={m} showCheckbox={false} />)}
                </div>
              </div>
            )}
          </div>

          {/* ── AVAILABLE MODELS ── */}
          <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-mono text-[#00D4FF]">AVAILABLE MODELS ({availableModels.length})</h3>
              {selected.size > 0 && (
                <button onClick={bulkActivate} disabled={bulkUpdating}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-mono bg-[#22C55E] text-black disabled:opacity-50">
                  {bulkUpdating ? 'Adding...' : `Add ${selected.size} to Catalog`}
                </button>
              )}
            </div>

            <TierPills active={availTier} onSelect={setAvailTier} counts={availTierCounts} />

            {/* Quick toggle filters */}
            <div className="flex gap-1.5 mt-2">
              <button onClick={() => setNewOnly(v => !v)}
                className={`px-2 py-0.5 text-[8px] font-mono border transition-colors ${
                  newOnly ? 'bg-[#22C55E] text-black border-[#22C55E]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#22C55E] hover:text-[#F5F1E8]'}`}>
                New 30d
              </button>
              <button onClick={() => setHasIqOnly(v => !v)}
                className={`px-2 py-0.5 text-[8px] font-mono border transition-colors ${
                  hasIqOnly ? 'bg-[#F2A900] text-black border-[#F2A900]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#F2A900] hover:text-[#F5F1E8]'}`}>
                Has IQ
              </button>
            </div>

            {/* Range filters */}
            <div className="grid grid-cols-4 gap-2 mt-2">
              {[
                { label: 'Min IQ', state: minIq, set: setMinIq,
                  opts: [{v:0,l:'Any'},{v:200,l:'20+'},{v:300,l:'30+'},{v:400,l:'40+'},{v:500,l:'50+'}] },
                { label: 'Min Context', state: minCtx, set: setMinCtx,
                  opts: [{v:0,l:'Any'},{v:32000,l:'32k+'},{v:64000,l:'64k+'},{v:128000,l:'128k+'},{v:500000,l:'500k+'}] },
                { label: 'Min Output', state: minOut, set: setMinOut,
                  opts: [{v:0,l:'Any'},{v:4096,l:'4k+'},{v:8192,l:'8k+'},{v:16384,l:'16k+'},{v:32768,l:'32k+'}] },
                { label: 'Capabilities', custom: (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {[['function_calling','Tools'],['thinking','Think'],['images','Images']].map(([v,l]) => (
                      <button key={v} onClick={() => setCapFilter(capFilter === v ? '' : v)}
                        className={`px-1.5 py-0.5 text-[8px] font-mono border transition-colors ${
                          capFilter === v ? 'bg-[#FF4D00] text-black border-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00] hover:text-[#F5F1E8]'}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                )},
              ].map(({ label, state, set, opts, extra, custom }) => (
                <div key={label} className="p-2 bg-[#0F0F0F] border border-[#2A2A2A]">
                  <p className="text-[8px] font-mono text-[#A0A0A0] mb-1">{label}</p>
                  {custom || (
                    <div className="flex flex-wrap gap-1">
                      {opts.map(o => (
                        <button key={o.v} onClick={() => set(o.v)}
                          className={`px-1.5 py-0.5 text-[8px] font-mono border transition-colors ${
                            state === o.v ? 'bg-[#FF4D00] text-black border-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00] hover:text-[#F5F1E8]'}`}>
                          {o.l}
                        </button>
                      ))}
                      {extra}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Search */}
            <div className="relative mt-2">
              <Search className="w-3 h-3 absolute left-2 top-2.5 text-[#A0A0A0]" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search models..."
                className="w-full pl-7 pr-8 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#00D4FF]" />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-2 text-[#A0A0A0] hover:text-[#F5F1E8]">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {availableModels.length === 0 ? (
              <p className="text-xs text-[#A0A0A0] py-3 mt-2">
                {models.filter(m => !m.catalog_enabled && !m.deprecated_at).length === 0 ? 'All available models are in the catalog.' : 'No models match the current filters.'}
              </p>
            ) : (
              <div className="mt-2">
                <button onClick={toggleSelectAll}
                  className="flex items-center gap-2 w-full px-2 py-1 text-left text-[9px] font-mono text-[#A0A0A0] hover:text-[#F5F1E8] transition-colors">
                  {selected.size === availableModels.length
                    ? <CheckSquare className="w-3.5 h-3.5 text-[#FF4D00]" />
                    : <Square className="w-3.5 h-3.5" />}
                  {selected.size === availableModels.length ? 'Deselect all' : `Select all ${availableModels.length} visible`}
                </button>
                {/* #1044 — sticky header inside the scroll container for column alignment */}
                <div className="max-h-80 overflow-y-auto mt-0.5" style={{ scrollbarGutter: 'stable' }}>
                  <div className="sticky top-0 bg-[#1A1A1A] z-10">
                    <ColHeaders showCheckbox={true} />
                  </div>
                  <div className="space-y-0.5 mt-0.5">
                    {availableModels.slice(0, 200).map(m => <ModelRow key={m.id} m={m} showCheckbox={true} />)}
                    {availableModels.length > 200 && (
                      <p className="text-[9px] text-[#A0A0A0] text-center py-1">Showing 200 of {availableModels.length} — refine filters</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
